import React, { useMemo, useRef, useState } from 'react';
import { MessageSquare } from '../../assets/AppIcons';
import CommentComposer from './CommentComposer';
import CommentItem from './CommentItem';
import { getCommentId, normalizeCommentTree } from './commentThread.helpers';
import './commentThread.css';

const CommentIcon = MessageSquare || (() => null);

export default function CommentThread({
  title = 'Commenti',
  comments = [],
  currentUser = null,
  staff = [],
  isLoading = false,
  isSubmitting = false,
  error = '',
  emptyLabel = 'Nessun commento presente.',
  placeholder = 'Scrivi un commento...',
  submitLabel = 'Invia commento',
  onSubmitComment,
  onSubmitReply,
  onDeleteComment,
  allowReplies = false,
  readOnly = false,
  disabled = false,
  entityType = '',
  entityId = '',
  autoFocus = false,
}) {
  const [replyTarget, setReplyTarget] = useState(null);
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [replySubmittingTo, setReplySubmittingTo] = useState(null);
  const submitLockRef = useRef(false);
  const replyLockRef = useRef(false);
  const normalizedComments = useMemo(() => normalizeCommentTree(comments), [comments]);
  const count = (comments || []).length;
  const blocked = disabled || readOnly || isSubmitting || internalSubmitting;

  async function submitComment(payload) {
    if (submitLockRef.current) return { ok: false };
    submitLockRef.current = true;
    setInternalSubmitting(true);
    try {
      return await onSubmitComment?.(payload);
    } finally {
      submitLockRef.current = false;
      setInternalSubmitting(false);
    }
  }

  async function submitReply(parentId, payload) {
    if (replyLockRef.current) return { ok: false };
    replyLockRef.current = true;
    setReplySubmittingTo(parentId);
    try {
      const result = await onSubmitReply?.(parentId, payload);
      if (result?.ok !== false) setReplyTarget(null);
      return result;
    } finally {
      replyLockRef.current = false;
      setReplySubmittingTo(null);
    }
  }

  return (
    <section
      className="comment-thread"
      data-entity-type={entityType || undefined}
      data-entity-id={entityId || undefined}
    >
      <header className="comment-thread__header">
        <div>
          <CommentIcon size={16} />
          <h3>{title} ({count})</h3>
        </div>
      </header>

      {error ? (
        <div role="alert" className="comment-thread__error wt-inline-error">{error}</div>
      ) : null}

      <div className="comment-thread__list">
        {isLoading ? (
          <div className="comment-thread__empty">Caricamento...</div>
        ) : null}
        {!isLoading && normalizedComments.length === 0 ? (
          <div className="comment-thread__empty">{emptyLabel}</div>
        ) : null}
        {!isLoading && normalizedComments.map((comment) => (
          <CommentItem
            key={getCommentId(comment)}
            comment={comment}
            staff={staff}
            currentUser={currentUser}
            allowReplies={!readOnly && allowReplies && Boolean(onSubmitReply)}
            replyTargetId={replyTarget ? getCommentId(replyTarget) : null}
            replySubmittingTo={replySubmittingTo}
            onStartReply={setReplyTarget}
            onCancelReply={() => setReplyTarget(null)}
            onSubmitReply={submitReply}
            onDeleteComment={readOnly ? undefined : onDeleteComment}
            disabled={blocked}
          />
        ))}
      </div>

      {!readOnly ? (
        <div className="comment-thread__composer">
          <CommentComposer
            key={`${entityType}:${entityId}`}
            autoFocus={autoFocus}
            currentUser={currentUser}
            staff={staff}
            placeholder={placeholder}
            submitLabel={submitLabel}
            disabled={disabled}
            isSubmitting={isSubmitting || internalSubmitting}
            onSubmit={submitComment}
          />
        </div>
      ) : null}
    </section>
  );
}
